import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAppointment } from './appointmentContext';
import { usePostMessage, useReceiveMessage } from './messageContext';
import { sessionApi, appointmentPath, isNotAvailable } from '../helpers/sessionApi';
import { SESSION_STATE_EVENT, extractSessionStateMessage } from '../helpers/sessionStateMessages';

export { SESSION_STATE_EVENT, extractSessionStateMessage };

// Shared session state (contract C, BE-7):
//   GET/PATCH /api/appointments/<id>/session_state/   (PATCH = partial merge, any participant)
//   server broadcasts {"type":"session.state","appointment":id,"state":{...}} on ws/chat/<room>/
//   POST /api/appointments/<id>/session_events/ {event:"joined"|"left"|"started"|"ended"} (role derived server-side)
//
// Read-only keys (started_at, ended_at, updated_at, updated_by) and unknown keys are rejected by
// the API (400) - only PATCH step, sub_step, test_key, calibration, distance, auto_resize.
// Until the endpoint exists (404), patches are applied locally and relayed to the other
// participant over the existing room protocol as {type:'session.state', state} so both sides
// still stay in step.

export const DEFAULT_SESSION_STATE = {
  step: null,
  sub_step: null,
  test_key: null,
  calibration: { method: null, device: null, screen_calibrated: false, camera_calibrated: false, card_ratio: null },
  distance: { mode: 'face', mm: null, positioned: false },
  auto_resize: true,
  started_at: null,
  ended_at: null,
  updated_at: null,
  updated_by: null,
};

const NESTED = ['calibration', 'distance'];

// Partial merge like the server: top-level keys replace, `calibration`/`distance` merge one level.
export const mergeSessionState = (base, partial) => {
  if (!partial || typeof partial !== 'object') return base;
  const next = { ...base };
  Object.entries(partial).forEach(([key, value]) => {
    if (NESTED.includes(key) && value && typeof value === 'object' && !Array.isArray(value)) {
      next[key] = { ...(base?.[key] || {}), ...value };
    } else {
      next[key] = value;
    }
  });
  return next;
};

const SessionStateContext = createContext(null);

export const SessionStateProvider = ({ children }) => {
  const { appointment, userRole } = useAppointment() || {};
  const postMessage = usePostMessage();
  const receivedMessage = useReceiveMessage();
  const appointmentId = appointment?.currentAppointment?.id;
  const role = userRole === 'patient' || appointment?.currentUser?.role === 'patient' ? 'patient' : 'clinician';

  const [state, setState] = useState(DEFAULT_SESSION_STATE);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | unavailable | error
  const [error, setError] = useState(null);
  const [remoteVersion, setRemoteVersion] = useState(0); // bumps when the OTHER participant changed the state
  const statusRef = useRef(status);
  statusRef.current = status;

  const refresh = useCallback(async () => {
    if (!appointmentId) return null;
    setStatus((s) => (s === 'ready' ? s : 'loading'));
    try {
      const data = await sessionApi(appointmentPath(appointmentId, 'session_state/'));
      setState((s) => mergeSessionState(s, data || {}));
      setStatus('ready');
      setError(null);
      return data;
    } catch (e) {
      if (isNotAvailable(e)) setStatus('unavailable');
      else {
        setStatus('error');
        setError(e);
      }
      return null;
    }
  }, [appointmentId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyRemote = useCallback(
    (msg) => {
      if (!msg?.state) return;
      if (msg.appointment && appointmentId && String(msg.appointment) !== String(appointmentId)) return;
      setState((s) => mergeSessionState(s, msg.state));
      // The server echoes our own PATCHes to the whole room; only count the other side's changes.
      const by = msg.state.updated_by;
      const own = by ? (by.role === 'patient') === (role === 'patient') : msg.senderRole === role;
      if (!own) setRemoteVersion((v) => v + 1);
    },
    [appointmentId, role],
  );

  // Server broadcasts (forwarded by appointmentContext's socket listener).
  useEffect(() => {
    const onEvent = (e) => applyRemote(e.detail);
    window.addEventListener(SESSION_STATE_EVENT, onEvent);
    return () => window.removeEventListener(SESSION_STATE_EVENT, onEvent);
  }, [applyRemote]);

  // Fallback relay over the legacy room protocol.
  useEffect(() => {
    if (receivedMessage?.type === 'session.state' && receivedMessage?.senderRole !== role) applyRemote(receivedMessage);
  }, [receivedMessage, role, applyRemote]);

  const patch = useCallback(
    async (partial) => {
      setState((s) => mergeSessionState(s, partial));
      if (!appointmentId) return null;
      const relay = () => {
        try {
          postMessage?.({ type: 'session.state', senderRole: role, appointment: appointmentId, state: partial });
        } catch {
          /* socket not ready */
        }
      };
      if (statusRef.current === 'unavailable') {
        relay();
        return null;
      }
      try {
        const data = await sessionApi(appointmentPath(appointmentId, 'session_state/'), { method: 'PATCH', body: partial });
        if (data && typeof data === 'object') setState((s) => mergeSessionState(s, data));
        setStatus('ready');
        return data;
      } catch (e) {
        if (isNotAvailable(e)) {
          setStatus('unavailable');
          relay();
          return null;
        }
        setError(e);
        throw e;
      }
    },
    [appointmentId, postMessage, role],
  );

  const recordEvent = useCallback(
    async (event) => {
      if (!appointmentId) return;
      try {
        await sessionApi(appointmentPath(appointmentId, 'session_events/'), { method: 'POST', body: { event } });
      } catch {
        /* optional endpoint - ignore */
      }
    },
    [appointmentId],
  );

  const value = useMemo(
    () => ({ state, status, error, available: status === 'ready', patch, refresh, recordEvent, remoteVersion, role }),
    [state, status, error, patch, refresh, recordEvent, remoteVersion, role],
  );
  return <SessionStateContext.Provider value={value}>{children}</SessionStateContext.Provider>;
};

const fallback = {
  state: DEFAULT_SESSION_STATE,
  status: 'idle',
  error: null,
  available: false,
  patch: async () => null,
  refresh: async () => null,
  recordEvent: async () => {},
  remoteVersion: 0,
  role: 'clinician',
};

/**
 * useSessionState() -> { state, status, available, patch(partial), refresh(), recordEvent(event), remoteVersion, role }
 *   state   the merged session state (DEFAULT_SESSION_STATE shape)
 *   patch   optimistic partial merge + PATCH (falls back to a room relay while the API 404s)
 */
export const useSessionState = () => useContext(SessionStateContext) || fallback;

export default SessionStateProvider;
